import {Dialog, DialogTitle} from '@priority-software/priority-style-react';
import {observer} from 'mobx-react';
import React, {useEffect} from 'react';
import {useQuery} from 'react-query';
import usePortal from 'react-useportal';

import {getLocalizedText, texts} from '../../../constants/TextConst';
import {zoomToFieldValue, zoomToNewEntry} from '../../../_legacy/FieldSearch/actions/formSearch';
import {setIsNavigationEnabled} from '../../../_shared/GWTBridge/invoke/genPage';
import {setHasModalChild} from '../../../_shared/GWTBridge/invoke/netForm';
import {setActiveInputValue} from '../../../_shared/GWTBridge/invoke/priTextBox';
import {showConfirmMessage} from '../../../_shared/Messages/actions/MessageActions';
import {startRecordUndo} from '../../Form/actions/form';
import {ScreenLocker} from '../../Form/components/ScreenLocker';
import {endForm} from '../../../services/form/form';
import {APIActionStatus, Field, formsStore} from '../../../stores/Form';
import {RecordEditState} from '../../../stores/Form/enums';
import {CustomQueryClientProvider} from '../../../hooks/useQueryClientProvider';
import {executeDirectActivation, getRelevantColumns, openCustomerForm} from '../api';
import {CUSTOMER_FIELD_NAME, MAKE_CUST_NAME} from '../consts';
import Footer from './Footer';
import QuickCustomerForm from './Form';
import styles from './styles.module.scss';

interface Props {
    onClose: () => void;
}

const QuickCustomerModalComp = observer(({onClose}: Props) => {
    const {data: form, isLoading, isFetching} = useQuery(['Form'], openCustomerForm);
    const columns = getRelevantColumns(form?.columns);

    useEffect(() => {
        setIsNavigationEnabled(false);
        setHasModalChild(true);

        return () => {
            setIsNavigationEnabled(true);
            setHasModalChild(false);
        };
    }, []);

    const handleEndForm = async () => {
        const {session} = formsStore.currentScreen.getActiveForm();
        await endForm(session);
        formsStore.deleteForm(session);
    };

    const handleCloseWithoutSave = async () => {
        const form = formsStore.currentScreen.getActiveForm();
        const hasValues = form.records[0].fields.some(field => !!field.currentValue);
        if (formsStore.currentScreen.editMode !== RecordEditState.default || hasValues) {
            showConfirmMessage({
                title: getLocalizedText(texts.exitNoSaveMessage),
                onConfirm: async () => {
                    await startRecordUndo(form);
                    await handleClose();
                },
                confirmButtonText: getLocalizedText(texts.yes),
                cancleButtonText: getLocalizedText(texts.no),
                defaultButton: 0
            });
        } else {
            handleClose();
        }
    };

    const handleClose = async () => {
        await handleEndForm();
        onClose();
    };

    const getCustNumValue = () => {
        const record = formsStore.currentScreen.getActiveForm().records[0];
        const col = columns.find(col => col.Name === CUSTOMER_FIELD_NAME);
        const field = record.getField(col.ID as number);
        return field.origValue;
    };

    const handleZoom = async () => {
        if (formsStore.currentScreen.editMode !== RecordEditState.default) {
            const isAllowd = await handleSave();

            if (isAllowd === APIActionStatus.passed) {
                const value = getCustNumValue();
                setActiveInputValue(value, false);
                await handleClose();
                zoomToFieldValue();
            }
        } else {
            await handleClose();
            zoomToNewEntry();
        }
    };

    const handleSave = async (): Promise<APIActionStatus> => {
        return formsStore.currentScreen.saveRecord();
    };

    const onSave = async () => {
        const isAllowd = await handleSave();

        if (isAllowd === APIActionStatus.passed) {
            const value = getCustNumValue();
            setActiveInputValue(value, false);
            handleClose();
        }
    };

    const handleConvert = async () => {
        const isAllowd = await handleSave();

        if (isAllowd === APIActionStatus.passed) {
            const newCustNum = await executeDirectActivation(formsStore.currentForm);
            if (newCustNum) {
                setActiveInputValue(newCustNum, false);
            }
            handleClose();
        }
    };

    if (isLoading || isFetching) {
        return <ScreenLocker showLoader={isLoading} locked={isLoading} />;
    }

    const getIsSaveDisabled = () => {
        const record = formsStore.currentForm.records[0];
        return columns.some(col => {
            const field: Field = record.getField(col.id);
            return col.Mandatory && !field.origValue;
        });
    };
    const isSaveDisabled = getIsSaveDisabled();
    const hasMakeCustAction = formsStore.currentScreen
        .getActiveForm()
        .actions.some(act => act.Ename === MAKE_CUST_NAME);

    return (
        <Dialog
            componentClasses={styles.dialog}
            onClose={handleCloseWithoutSave}
            maskClosable={false}
            isOpen={true}
            title={
                <DialogTitle
                    textColor="primary-500"
                    skin="h2"
                    title={getLocalizedText(texts.createCustomerTitle)}
                />
            }
            content={<QuickCustomerForm columns={columns} />}
            footer={
                <Footer
                    hasAction={hasMakeCustAction}
                    isSaveDisabled={isSaveDisabled}
                    onClose={handleCloseWithoutSave}
                    onSave={onSave}
                    onSaveAndConvert={handleConvert}
                    onZoom={handleZoom}
                />
            }
        />
    );
});

const QuickCustomerModal = (props: Props) => {
    const {Portal} = usePortal();
    return (
        <Portal>
            <CustomQueryClientProvider>
                <QuickCustomerModalComp {...props} />
            </CustomQueryClientProvider>
        </Portal>
    );
};

export default QuickCustomerModal;
